/**
 * Meta Connection Context
 * 
 * Shared context for managing Meta Ads account connection across all analytics pages
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useAuth } from '@trafiflow/shared-ui'
import { metaApi } from '@/shared/services'

interface MetaConnectionState {
  connected: boolean
  loading: boolean
  syncing: boolean
  accountId?: string
  accountName?: string
  currency?: string
  lastSyncAt?: string
  connectedAt?: string
}

interface MetaConnectionContextType {
  connection: MetaConnectionState
  connect: (accessToken: string, accountId: string, accountName?: string, currency?: string) => Promise<void>
  disconnect: () => Promise<void>
  refresh: () => Promise<void>
  sync: () => Promise<void>
  smartSync: () => Promise<void>
}

const MetaConnectionContext = createContext<MetaConnectionContextType | undefined>(undefined)

export function MetaConnectionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [connection, setConnection] = useState<MetaConnectionState>({
    connected: false,
    loading: true,
    syncing: false,
  })

  const checkConnection = useCallback(async () => {
    // Don't check connection if user is not authenticated
    if (!isAuthenticated) {
      setConnection({
        connected: false,
        loading: false,
        syncing: false,
      })
      return
    }

    try {
      setConnection(prev => ({ ...prev, loading: true }))
      const response = await metaApi.getConnectionStatus()
      
      if (response.success && response.data) {
        setConnection({
          connected: response.data.connected || false,
          loading: false,
          syncing: false,
          accountId: response.data.account_id,
          accountName: response.data.account_name,
          currency: response.data.currency,
          lastSyncAt: response.data.last_sync_at,
          connectedAt: response.data.connected_at,
        })
      } else {
        setConnection({
          connected: false,
          loading: false,
          syncing: false,
        })
      }
    } catch {
      setConnection({
        connected: false,
        loading: false,
        syncing: false,
      })
    }
  }, [isAuthenticated])

  const connect = useCallback(async (
    accessToken: string,
    accountId: string,
    accountName?: string,
    currency?: string
  ) => {
    // Show syncing state - the backend auto-syncs on connect
    setConnection(prev => ({ ...prev, loading: true, syncing: true }))
    try {
      const response = await metaApi.connect({
        access_token: accessToken,
        account_id: accountId,
        account_name: accountName,
        currency: currency,
      })
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to connect')
      }
      
      // Connection includes auto-sync, now refresh to get updated status
      await checkConnection()
    } catch (error) {
      setConnection(prev => ({ ...prev, loading: false, syncing: false }))
      throw error
    }
  }, [checkConnection])

  const disconnect = useCallback(async () => {
    setConnection(prev => ({ ...prev, loading: true }))
    try {
      const response = await metaApi.disconnect()
      if (!response.success) {
        throw new Error(response.error || 'Failed to disconnect')
      }
      // Force refresh connection status from backend
      await checkConnection()
    } catch (error) {
      setConnection(prev => ({ ...prev, loading: false }))
      throw error
    }
  }, [checkConnection])

  const sync = useCallback(async () => {
    try {
      await metaApi.sync()
    } catch (error) {
      console.error('Error syncing Meta data:', error)
      throw error
    }
  }, [])

  const smartSync = useCallback(async () => {
    try {
      await metaApi.smartSync()
      await checkConnection()
    } catch (error) {
      console.error('Error smart syncing Meta data:', error)
      throw error
    }
  }, [checkConnection])

  useEffect(() => {
    // Only check connection after auth is loaded and user is authenticated
    if (!authLoading) {
      checkConnection()
    }
  }, [authLoading, isAuthenticated, checkConnection])

  return (
    <MetaConnectionContext.Provider value={{
      connection,
      connect,
      disconnect,
      refresh: checkConnection,
      sync,
      smartSync,
    }}>
      {children}
    </MetaConnectionContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMetaConnection() {
  const context = useContext(MetaConnectionContext)
  if (!context) {
    throw new Error('useMetaConnection must be used within MetaConnectionProvider')
  }
  return context
}

